/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, lazy} from 'react'
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom'
import {App} from '../App'
import {AuthPage, Logout, useAuth} from '../modules/auth'
import ForgotPasswordWrapper from '../modules/auth/forgot-password/ForgotPasswordPage'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import GymCheck from '../modules/gym/GymCheck'
import OrderWrapper from '../modules/order/Order'
import RatingWrapper from '../modules/rating/Rating'
import {PrivateRoutes} from './PrivateRoutes'
import OrderSelectOutletWrapper from '../modules/order/OrderSelectOutlet'
import PrivacyPolicyPage from '../modules/privacy-policy/PrivacyPolicyPage'
import SurveyCustomerWrapper from '../modules/survey-customer/SurveyCustomer'
import SurveyCustomerSelectOutletWrapper from '../modules/survey-customer/SurveyCustomerSelectOutlet'

const MapsView = lazy(() => import('@/app/modules/maps/Maps'))
const DailyCheckinPage = lazy(() => import('../modules/daily-checkin/DailyCheckinPage'))
const MinuteOfMeetingPage = lazy(() => import('../modules/minute-of-meeting/MinuteOfMeetingPage'))
const BriefingReportsPage = lazy(() => import('../modules/briefing-reports/BriefingReportsPage'))

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser, token} = useAuth()
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route path='gym/check' element={<GymCheck />} />
        <Route path='gym/check/:outletGuid' element={<GymCheck />} />
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          <Route path='forgot-password/:id' element={<ForgotPasswordWrapper />} />
          <Route path='order/:id' element={<OrderWrapper />} />
          <Route path='order' element={<OrderSelectOutletWrapper />} />
          <Route path='booking' element={<OrderSelectOutletWrapper />} />
          <Route path='survey-customer' element={<SurveyCustomerWrapper />} />
          <Route path='survey-customer/:id' element={<SurveyCustomerWrapper />} />
          <Route path='privacy-policy' element={<PrivacyPolicyPage />} />
          <Route path='map-view' element={<MapsView />} />
          <Route path='rating/:id' element={<RatingWrapper />} />
          {currentUser && token ? (
            <>
              <Route path='apps/daily-checkin/*' element={<DailyCheckinPage />} />
              <Route path='apps/minute-of-meeting/*' element={<MinuteOfMeetingPage />} />
              <Route path='apps/briefing-reports/*' element={<BriefingReportsPage />} />
              <Route
                path='apps/minutes-of-meeting/*'
                element={<Navigate to='/apps/minute-of-meeting' replace />}
              />
              <Route
                path='apps/briefing-report/*'
                element={<Navigate to='/apps/briefing-reports' replace />}
              />
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='/dashboard' />} />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
